import React, { useState, useEffect } from "react";
import axios from "axios";
import "./CommissionRateManager.css";
import DataTable from "react-data-table-component"; // Import DataTable
import { baseUrl } from "../../../enum/constant";

const CommissionRateManager = () => {
  const [formData, setFormData] = useState({
    commission_rate: "", // Set initial value as empty string
    commission_value: "", // Set initial value as empty string
    buyPairs: "USD-BTC",
    sellPairs: "BTC-USD",
    type: "buy", // Default to 'buy'
  });
  const [message, setMessage] = useState("");
  const [commissionRates, setCommissionRates] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [filterText, setFilterText] = useState(""); // For storing the filter text

  // Fetch existing commission rates when the component mounts
  const fetchCommissionRates = async () => {
    try {
      const response = await axios.get(baseUrl + "api/commission-rates");
      if (response.data && Array.isArray(response.data)) {
        setCommissionRates(response.data); // Update commissionRates with the fetched data
      }
    } catch (error) {
      console.error("Error fetching commission rates:", error);
      setMessage("Error fetching existing data.");
    }
  };

  useEffect(() => {
    fetchCommissionRates(); // Fetch commission rates on component mount
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    setErrorMessage(""); // Reset error message on every input change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Regex for validating format like '0.5 - 1'
    const regex = /^\d+(\.\d+)?-\d+(\.\d+)?$/;

    // Check if commission_rate matches the desired format
    if (!regex.test(formData.commission_rate)) {
      setErrorMessage("Please use the format: 0.5-1");
      return; // Stop form submission if the format is incorrect
    }

    try {
      const dataToSend = {
        commission_rate: formData.commission_rate, // Send the value as it is
        commission_value: formData.commission_value,
        makePairs: formData.type == 'buy' ? formData.buyPairs : formData.sellPairs,
        type: formData.type || "buy", // Include the type (buy/sell)
      };

      if (editingId) {
        // If editing an existing commission rate, make PUT request
        const response = await axios.put(
          `${baseUrl}api/update-commission-rates/${editingId}`,
          dataToSend
        );
        setMessage("Commission range updated successfully!");
      } else {
        // POST request to create a new commission rate
        const response = await axios.post(
          baseUrl + "api/commission-rates",
          dataToSend
        );
        setMessage("Commission range saved successfully!");
      }

      // Refresh commission rates
      fetchCommissionRates();

      // Reset form data and editing state
      setFormData({
        commission_rate: "",
        commission_value: "",
        buyPairs: "USD-BTC",
        sellPairs: "BTC-USD",
        type: "buy", // Reset to default
      });
      setEditingId(null);

      // Hide message after 3 seconds
      setTimeout(() => {
        setMessage("");
      }, 3000);
    } catch (error) {
      console.error("Error submitting form data:", error);
      setMessage("Error submitting form data.");
      setTimeout(() => {
        setMessage("");
      }, 3000);
    }
  };

  const handleEdit = (id) => {
    const selectedRate = commissionRates.find((rate) => rate.id === id);
    if (selectedRate) {
      const obj = {
      }
      if (selectedRate.type == 'buy') {
        obj.buyPairs = selectedRate.makePairs
      } else {
        obj.sellPairs = selectedRate.makePairs
      }
      setFormData({
        commission_rate: selectedRate.commission_rate, // Set the current rate
        commission_value: selectedRate.commission_value, // Set the current value
        ...obj,
        type: selectedRate.type || "buy", // Set the current type, default to 'buy'
      });
      setEditingId(id); // Set editing ID to update
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this commission rate?")
    ) {
      try {
        await axios.delete(`${baseUrl}api/delete-commission-rates/${id}`);
        setMessage("Commission rate deleted successfully!");
        fetchCommissionRates(); // Refresh the list after deleting
        setTimeout(() => {
          setMessage("");
        }, 3000);
      } catch (error) {
        console.error("Error deleting commission rate:", error);
        setMessage("Error deleting commission rate.");
        setTimeout(() => {
          setMessage("");
        }, 3000);
      }
    }
  };

  // Define columns for the DataTable
  const columns = [
    {
      name: "No",
      selector: (row, index) => index + 1,
      sortable: true,
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
    },
    {
      name: "Pairs",
      selector: (row) => row.makePairs,
      sortable: true,
    },
    {
      name: "Range",
      selector: (row) => row.commission_rate,
      sortable: true,
    },
    {
      name: "Value",
      selector: (row) => row.commission_value,
      sortable: true,
    },
    {
      name: "Actions",
      button: true,
      cell: (row) => (
        <div id="btnid" style={{ display: "contents" }}>
          <button
            className="badge bg-warning m-1 p-2 text-light"
            style={{ border: "none" }}
            onClick={() => handleEdit(row.id)}
          >
            Update
          </button>
          <button
            className="badge bg-danger p-2 text-light mr-3"
            style={{ border: "none" }}
            onClick={() => handleDelete(row.id)}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  // Filter commissionRates based on filterText
  const filteredData = commissionRates.filter(
    (rate) =>
      rate.makePairs.toLowerCase().includes(filterText.toLowerCase()) ||
      rate.commission_rate.toLowerCase().includes(filterText.toLowerCase()) ||
      rate.commission_value.toLowerCase().includes(filterText.toLowerCase()) ||
      rate.type.toLowerCase().includes(filterText.toLowerCase())
  );

  return (
    <div className="cus-dash-container container">
      <div className="row">
        <div className="col-12">
          {message && (
            <div
              className={`cus-alert-msg ${message.includes("successfully")
                ? "alert-success"
                : message.includes("Error")
                  ? "alert-danger"
                  : "alert-success"
                }`}
              role="alert"
            >
              <div class="icon">
                {message.includes("successfully") ? "✔" : "✖"}
              </div>
              <p class="message mb-0">{message}</p>
            </div>
          )}
          <h5 className="card-title text-justify">Range Information</h5>
          <div className="card mt-2 border-0">
            <div className="card-body">
              <form onSubmit={handleSubmit} style={{ textAlign: "start" }}>
                <div className="row">
                  <div className="col-md-3 mb-4">
                    <label className="form-label fw-semibold text-dark fs-6">
                      Transaction Type
                    </label>
                    <div className="d-flex gap-3 mt-2">
                      {/* Buy Option */}
                      <div className="form-check custom-radio-wrapper">
                        <input
                          className="form-check-input d-none"
                          type="radio"
                          name="type"
                          id="buyType"
                          value="buy"
                          checked={formData.type === "buy"}
                          onChange={handleChange}
                        />
                        <label
                          className={`form-check-label btn btn-outline-success px-4 py-2 rounded-pill shadow-sm ${formData.type === "buy" ? "active" : ""
                            }`}
                          htmlFor="buyType"
                        >
                          Buy
                        </label>
                      </div>

                      {/* Sell Option */}
                      <div className="form-check custom-radio-wrapper">
                        <input
                          className="form-check-input d-none"
                          type="radio"
                          name="type"
                          id="sellType"
                          value="sell"
                          checked={formData.type === "sell"}
                          onChange={handleChange}
                        />
                        <label
                          className={`form-check-label btn btn-outline-danger px-4 py-2 rounded-pill shadow-sm ${formData.type === "sell" ? "active" : ""
                            }`}
                          htmlFor="sellType"
                        >
                          Sell
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-3 mb-3">
                    <label htmlFor="makePairs" className="form-label">
                      Set Pair
                    </label>
                    {formData.type === "buy" ? (
                      <select
                        className="form-control"
                        name="buyPairs"
                        value={formData.buyPairs}
                        onChange={handleChange}
                        required
                      >
                        <option value="USD-BTC">USD-BTC</option>
                        <option value="USD-USDT">USD-USDT</option>
                        <option value="USD-ETH"> USD-ETH</option>
                        <option value="USD-USDC">USD-USDC</option>
                        <option value="EUR-BTC">EUR-BTC</option>
                        <option value="EUR-USDT">EUR-USDT</option>
                        <option value="EUR-ETH"> EUR-ETH</option>
                        <option value="EUR-USDC">EUR-USDC</option>
                      </select>
                    ) : (
                      <select
                        className="form-control"
                        name="sellPairs"
                        value={formData.sellPairs}
                        onChange={handleChange}
                        required
                      >
                        <option value="BTC-USD">BTC-USD</option>
                        <option value="USDT-USD">USDT-USD</option>
                        <option value="ETH-USD">ETH-USD</option>
                        <option value="USDC-USD">USDC-USD</option>
                        <option value="BTC-EUR">BTC-EUR</option>
                        <option value="USDT-EUR">USDT-EUR</option>
                        <option value="ETH-EUR">ETH-EUR</option>
                        <option value="USDC-EUR">USDC-EUR</option>
                      </select>
                    )}
                  </div>

                  <div className="col-md-2 mb-3">
                    <label htmlFor="commissionRange" className="form-label">
                      Range
                    </label>
                    <input
                      type="text"
                      placeholder="0.5 - 1"
                      className="form-control"
                      id="commission_rate"
                      name="commission_rate"
                      value={formData.commission_rate}
                      onChange={handleChange}
                      required
                    />
                    {errorMessage && (
                      <div className="text-danger mt-2">{errorMessage}</div>
                    )}
                  </div>
                  <div className="col-md-2 mb-3">
                    <label htmlFor="commission_value" className="form-label">
                      Value
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="commission_value"
                      name="commission_value"
                      placeholder="0.1"
                      value={formData.commission_value}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-3 mt-4">
                    <button type="submit" className="btn btn-primary">
                      {editingId ? "Update" : "Save/Update"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          {/* Commission Ranges Table */}
          <div className="mt-4">
            {/* Add a filter input above the table */}
            <div className="mb-3">
              <input
                type="text"
                placeholder="Search by Pair, Type, Range, or Value"
                className="form-control"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>

            <DataTable
              title="Commission Rates"
              columns={columns}
              data={filteredData}
              pagination
              paginationPerPage={10}
              paginationRowsPerPageOptions={[5, 10, 15, 20]}
              highlightOnHover
              fixedHeader
              searchable
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionRateManager;
